import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import type { ReactNode } from 'react';
import { Select } from 'antd';
import { CheckCircleIcon, StepOrgUserIcon } from '../../assets/icons';
import Breadcrumbs from '../../components/Breadcrumbs';

const FIELD =
  'w-full rounded-lg border border-line bg-white px-4 py-3.5 text-base text-ink placeholder:text-[#6b7280] transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';

const ROLES = ['State Admin', 'Observer', 'IT Auditor'];
const LEVELS = ['Alwar', 'Jaipur', 'Ajmer', 'Mumbai', 'Pune', 'Nagpur'];

interface UserFormValues {
  fullName: string;
  email: string;
  contact: string;
  role: string;
  orgLevel: string;
  active: boolean;
}

// Validation wired but errors intentionally not surfaced ("hide validations").
const validationSchema = Yup.object({
  fullName: Yup.string().required('Full name is required'),
  email: Yup.string().email('Enter a valid email address'),
});

function Field({ label, htmlFor, children }: { label: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={htmlFor} className="text-sm font-semibold text-ink-900">
        {label}
      </label>
      {children}
    </div>
  );
}

export default function CreateUserPage() {
  const navigate = useNavigate();

  const formik = useFormik<UserFormValues>({
    initialValues: {
      fullName: '',
      email: '',
      contact: '',
      role: '',
      orgLevel: '',
      active: true,
    },
    validationSchema,
    onSubmit: (values) => {
      // TODO: wire to a createUser mutation once the API exists.
      // eslint-disable-next-line no-console
      console.log('Create user', values);
      navigate('/admin/users');
    },
  });

  const { values, handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched } = formik;

  return (
    <div className="flex flex-col gap-6">
      {/* Breadcrumb + heading */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs items={[{ label: 'Users', to: '/admin/users' }, { label: 'Create New User' }]} />
        <h1 className="pt-1 text-[32px] font-bold leading-10 tracking-[-0.64px] text-ink-900">
          Create New User
        </h1>
        <p className="text-base text-ink-secondary">
          Register a new administrator or regional officer to the CHUNAAV platform.
        </p>
      </div>

      {/* Form card */}
      <form
        onSubmit={handleSubmit}
        noValidate
        className="overflow-hidden rounded-xl border border-line/30 bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_4px_6px_-2px_rgba(0,0,0,0.05)]"
      >
        {/* Card header */}
        <div className="flex items-center justify-between border-b border-line/30 bg-brand-shade px-6 py-6">
          <div className="flex items-center gap-3">
            <span className="flex size-10 items-center justify-center rounded-lg bg-[#dbeafe] text-brand">
              <StepOrgUserIcon className="h-[18px] w-[25px]" />
            </span>
            <h2 className="text-xl font-semibold tracking-[-0.2px] text-ink-900">User Details</h2>
          </div>
          <span className="rounded-full bg-brand-shade px-3 py-1.5 text-[10px] font-bold uppercase tracking-[1px] text-ink-secondary">
            System Record #842
          </span>
        </div>

        {/* Form body */}
        <div className="flex flex-col gap-8 p-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Field label="Full Name" htmlFor="fullName">
              <input
                id="fullName"
                name="fullName"
                placeholder="e.g. Rajesh Kumar"
                value={values.fullName}
                onChange={handleChange}
                onBlur={handleBlur}
                className={FIELD}
              />
            </Field>

            <Field label="Email Address" htmlFor="email">
              <input
                id="email"
                name="email"
                type="email"
                placeholder="rajesh.k@chunaav.gov.in"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={FIELD}
              />
            </Field>

            <Field label="Contact Number" htmlFor="contact">
              <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-ink-secondary">
                  +91
                </span>
                <input
                  id="contact"
                  name="contact"
                  placeholder="98765 43210"
                  value={values.contact}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={[FIELD, 'pl-[57px]'].join(' ')}
                />
              </div>
            </Field>

            <Field label="Role" htmlFor="role">
              <Select
                className="w-full"
                placeholder="Select Role"
                value={values.role || undefined}
                onChange={(v) => setFieldValue('role', v)}
                onBlur={() => setFieldTouched('role', true)}
                options={ROLES.map((r) => ({ value: r, label: r }))}
              />
            </Field>
          </div>

          <Field label="Organization Level" htmlFor="orgLevel">
            <Select
              className="w-full"
              placeholder="Select Level"
              value={values.orgLevel || undefined}
              onChange={(v) => setFieldValue('orgLevel', v)}
              onBlur={() => setFieldTouched('orgLevel', true)}
              options={LEVELS.map((l) => ({ value: l, label: l }))}
            />
          </Field>

          <div className="h-px w-full bg-line/30" />

          {/* Status toggle */}
          <div className="flex items-center justify-between gap-4 rounded-xl border border-line/20 bg-brand-shade p-4">
            <div className="flex items-center gap-4">
              <span className="size-3 shrink-0 rounded-full bg-brand" />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-ink-900">Status</span>
                <span className="text-sm text-ink-secondary">
                  User will be created as{' '}
                  <span className="font-bold text-brand">{values.active ? 'Active' : 'Inactive'}</span>
                </span>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={values.active}
              onClick={() => setFieldValue('active', !values.active)}
              className={[
                'relative h-8 w-14 shrink-0 rounded-full transition-colors',
                values.active ? 'bg-brand' : 'bg-[#c2c6d6]',
              ].join(' ')}
            >
              <span
                className={[
                  'absolute top-1 size-6 rounded-full bg-white transition-all',
                  values.active ? 'left-[28px]' : 'left-1',
                ].join(' ')}
              />
            </button>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/admin/users')}
              className="rounded-lg border border-line px-[33px] py-[13px] text-sm font-semibold text-ink-secondary transition-colors hover:bg-brand-shade"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 rounded-lg bg-brand px-10 py-3 text-sm font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark"
            >
              Save User
              <CheckCircleIcon className="size-[15px]" />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
